/**
 * useReviewSession - セッション単位の評価レビュー詳細取得フック
 *
 * バックエンド API は B 系レーンで実装予定。暫定的に
 *   GET /api/v1/sessions/{sessionId}/review
 * に axios でアクセスし、404 等で取得失敗時は未実装メッセージを表示する。
 *
 * レスポンス形式は ReviewSessionDetailResponse を想定（frontend/ の同型に準拠）。
 * API 実装側はこの shape に合わせて返却すれば UI が繋がる。
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/client';
import type { ReviewSessionDetailResponse } from '@/types/review';

export interface UseReviewSessionResult {
  detail: ReviewSessionDetailResponse | null;
  loading: boolean;
  error: string | null;
  /** 明示的な refetch。skip / complete 直後に呼んで backend の削除・遷移を画面に反映させる */
  refresh: () => void;
}

export function useReviewSession(sessionId: string | undefined): UseReviewSessionResult {
  const [detail, setDetail] = useState<ReviewSessionDetailResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(sessionId));
  const [error, setError] = useState<string | null>(null);
  // refetch trigger。skip / complete 完了直後に increment されるので useEffect が再走する。
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setDetail(null);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setDetail(null);

    api
      .get<ReviewSessionDetailResponse>(`/sessions/${sessionId}/review`, {
        signal: controller.signal,
      })
      .then((resp) => {
        setDetail(resp.data);
      })
      .catch((err: unknown) => {
        // キャンセルは無視
        if ((err as { name?: string }).name === 'CanceledError') return;
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) {
          setError('このセッションのフィードバック詳細はまだ生成されていません');
        } else if (status === 501) {
          setError('フィードバック詳細 API は未実装です（B系レーンで実装予定）');
        } else {
          const msg =
            (err as { response?: { data?: { message?: string; detail?: string } } }).response?.data
              ?.message ??
            (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ??
            (err as Error).message ??
            'フィードバック詳細の取得に失敗しました';
          setError(msg);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [sessionId, refreshTick]);

  const refresh = useCallback(() => {
    setRefreshTick((tick) => tick + 1);
  }, []);

  return { detail, loading, error, refresh };
}
